import { BooleanInput, coerceBooleanProperty, coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { ChangeDetectorRef, Directive, EventEmitter, Host, Input, Output, SimpleChanges } from '@angular/core';
import { Item } from '../../listbox/listbox.component';
import { ListComponent } from '../list.component';
import { Subscription, Subject, pipe, debounceTime } from 'rxjs';
import { RememberService } from '@app/core/services/remember.service';

@Directive({
    selector: 'tt-list[ttRadioSelection]:not(tt-list[ttCheckboxSelection])',
    exportAs: 'radioList',
})
export class ListRadioSelectionDirective<TType extends Item, TValue extends any> {
    /**
     * The id to store the last selected id in 616 with.
     */
    @Input()
    public ttRememberId: string = '';

    /**
     * Milliseconds between the storing of each change if ttRememberId is provided.
     *
     * @default 500
     */
    @Input()
    public set ttRememberDebounceTime(value: NumberInput) {
        this._rememberDebounceTime = coerceNumberProperty(value);
    }
    public get ttRememberDebounceTime(): number {
        return this._rememberDebounceTime;
    }
    private _rememberDebounceTime: number = 500;

    /**
     * Whether to only remember and not retrieve the last status upon initialization.
     *
     * @default true
     */
    @Input()
    public set ttOnlyRemember(value: BooleanInput) {
        this._onlyRemember = coerceBooleanProperty(value);
    }
    public get ttOnlyRemember(): boolean {
        return this._onlyRemember;
    }
    private _onlyRemember = true;

    /**
     * Configures radio selection of the list.
     */
    @Input()
    set ttRadioSelection(value: BooleanInput) {
        this._radioSelection = coerceBooleanProperty(value);
    }
    get ttRadioSelection(): boolean {
        return this._radioSelection;
    }
    _radioSelection = false;

    /**
     * The id of the selected list item.
     */
    @Input()
    public ttSelectedId?: TValue;

    /**
     * Event emitted when the radio selection has changed.
     */
    @Output()
    public ttSelectedIdChange = new EventEmitter<TValue>();

    /**
     * Event emitted when the radio selection of the list has changed.
     */
    @Output()
    ttRadioSelectionChanged = new EventEmitter<TType>();

    private ttRadioSelectionChangedSubscription?: Subscription;

    public rememberSubject = new Subject<unknown>();

    private rememberSubscription?: Subscription;

    constructor(
        @Host() private componentRef: ListComponent<TType>,
        private cdr: ChangeDetectorRef,
        private remember: RememberService,
    ) {}

    protected rememberModel(value: unknown) {
        if (!!this.ttRememberId) {
            this.remember.remember(this.ttRememberId, value);
        }
    }

    private async getLastModelState() {
        try {
            const value = (await this.remember.getLastStatus(this.ttRememberId))[0].variablevalue;

            if (!!value) {
                this.componentRef.radioChecked = value;
                this.ttSelectedId = value;
                this.ttSelectedIdChange.emit(value);
            }
        } catch (error) {
            console.error(error);
        }
    }

    ngOnInit(): void {
        this.componentRef.ttRadioSelection = this.ttRadioSelection;

        if (!!this.ttSelectedId && this.ttRadioSelection) {
            this.componentRef.radioChecked = this.ttSelectedId as string;
        }

        if (this.ttRadioSelection && !this.ttOnlyRemember && this.ttRememberId) {
            this.getLastModelState();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttOnlyRemember']) {
            this.ttOnlyRemember = changes['ttOnlyRemember'].currentValue;
        }

        if (changes['ttRememberDebounceTime']) {
            this.ttRememberDebounceTime = changes['ttRememberDebounceTime'].currentValue;
        }

        if (changes['ttRememberId'] || changes['ttRememberDebounceTime']) {
            this.rememberSubscription?.unsubscribe?.();

            if (this.ttRememberId) {
                this.rememberSubscription = this.rememberSubject.pipe(debounceTime(this.ttRememberDebounceTime)).subscribe({ next: (value) => this.rememberModel(value) });
            }
        }

        if (changes['ttSelectedId']) {
            this.componentRef.radioChecked = this.ttSelectedId as string;
        }

        if (changes['ttRadioSelection']) {
            this.ttRadioSelection = changes['ttRadioSelection'].currentValue;
            this.componentRef.ttRadioSelection = this.ttRadioSelection;

            if (this.ttRadioSelection) {
                this.ttRadioSelectionChangedSubscription = this.componentRef.ttRadioSelectionChanged.subscribe({
                    next: (value: TType) => {
                        this.ttSelectedId = value?.[this.componentRef.ttDataId] as TValue;
                        this.ttSelectedIdChange.emit(this.ttSelectedId);
                        this.ttRadioSelectionChanged.emit(value);
                        this.rememberSubject.next(this.ttSelectedId);
                    },
                });
            } else {
                this.ttRadioSelectionChangedSubscription?.unsubscribe();
            }

            this.cdr.detectChanges();
        }
    }
}
